import { useMutation, UseMutationOptions, useQueryClient, InfiniteData } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { instructionsApi } from 'src/Services/API';
import { InstructionPreviewDto } from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';

type InstructionsPage = {
  items: InstructionPreviewDto[];
  currentPage: number;
  pageCount: number;
};

type InstructionsQueryData = InfiniteData<InstructionsPage>;

type MutationContext = {
  previousInstructions?: InstructionsQueryData;
};

export const useDeleteInstruction = (
  options?: UseMutationOptions<void, unknown, string, MutationContext>
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<void, unknown, string, MutationContext>(
    ['delete-instruction', assetId],
    async (instructionId: string) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await instructionsApi.instructionsControllerRemove(assetId, instructionId, {
        transformResponse: [],
      });
      return response.data;
    },
    {
      ...options,
      // Add optimistic update
      onMutate: async instructionId => {
        options?.onMutate?.(instructionId);

        // Cancel any outgoing refetches to avoid overwriting our optimistic update
        await queryClient.cancelQueries(['fetch-instructions', assetId]);

        // Snapshot the previous value
        const previousInstructions = queryClient.getQueryData<InstructionsQueryData>([
          'fetch-instructions',
          assetId,
        ]);

        // Optimistic update - immediately update UI
        queryClient.setQueryData<InstructionsQueryData | undefined>(
          ['fetch-instructions', assetId],
          oldData => {
            if (!oldData) return oldData;

            // For infinite queries, we need to update each page
            return {
              ...oldData,
              pages: oldData.pages.map(page => ({
                ...page,
                items: page.items.filter(instruction => instruction.id !== instructionId),
              })),
              pageParams: [...oldData.pageParams],
            };
          }
        );

        // Return context with the previous data for rollback in case of error
        return { previousInstructions };
      },
      // Handle errors - rollback to the previous state if mutation fails
      onError: (_err, _instructionId, context) => {
        if (context?.previousInstructions) {
          queryClient.setQueryData<InstructionsQueryData>(
            ['fetch-instructions', assetId],
            context.previousInstructions
          );
        }
      },
      // Refetch after successful mutation to synchronize with the server
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(['fetch-instructions', assetId]);
        queryClient.invalidateQueries(['fetch-asset-details', assetId]);
        options?.onSuccess?.(data, variables, context);
        trackEvent('Instruction', 'delete');
      },
    }
  );
};
